import React from "react";
import {
  Box,
  HStack,
  Text,
  Flex,
  VStack,
  Button,
  Badge,
  Heading,
  AvatarGroup,
  IconButton,
  Card,
  Separator,
  SimpleGrid,
  Icon,
} from "@chakra-ui/react";
import { Avatar } from "../../ui/avatar";
import { Tooltip } from "../../ui/tooltip";
import { Student, Guardian, User } from "../../../services/Dtos";
import {
  calculateAge,
  getStudentClassNumberMessage,
} from "../../../services/Utils";
import GuardianCard from "./GuardianCard";
import { useStyles } from "../../../hooks/useStyles";
import useThemeColors from "../../../hooks/useThemeColors";
import useIcons from "../../../hooks/useIcons";
import Cad from "../../CustomUi/Cad";
import { genderMap } from "../../../services/constants";
import { useRoleAccess } from "../../../hooks/useRoleAccess";
import { Role } from "../../../services/Enums";

interface StudentInfoPanelProps {
  selectedStudent: Student;
  user: User | null;
  classMap: { [key: string]: string };
  handleEditStudent: (student: Student) => void;
  handleSameClass: (studentClass: string) => void;
  classCount: (student: Student) => number;
  firstFiveClassStudents: (student: Student) => Student[];
  handleAddOpenGuardian: () => void;
  handleEditGuardian: (guardian: Guardian) => void;
  handleViewResults: (student: Student) => void;
  editorPolicy: number[];
  altColor: string;
  brandTransparentColor: string;
  greenTransparentColor: string;
  scrollColor: string;

  studentStatusMap: { [key: number]: { color: string; name: string } };
  accentColor: string;
}

const StudentInfoPanel: React.FC<StudentInfoPanelProps> = ({
  selectedStudent,
  user,
  classMap,
  handleEditStudent,
  handleSameClass,
  classCount,
  firstFiveClassStudents,
  handleAddOpenGuardian,
  handleEditGuardian,
  handleViewResults,
  editorPolicy,

  studentStatusMap,
  accentColor,
}) => {
  const colors = useThemeColors();
  const { scrollBar } = useStyles();
  const icons = useIcons();
  const { hasRoleOrHigher } = useRoleAccess();
  const fourClassStudents = firstFiveClassStudents(selectedStudent).slice(0, 4);

  return (
    <Box
      flex={1}
      bg={colors.background.primary}
      borderRadius="md"
      width="100%"
      maxH="100%"
      overflowY="auto"
      ml={{ base: 0, lg: 3 }}
      css={scrollBar}
      overflowX="hidden"
    >
      <VStack gap={6} align="stretch">
        {/* Student Header Card */}

        <Cad position="sticky" top={0} zIndex={2} bg={colors.background.subtle}>
          <HStack gap={4} align="start">
            <Avatar
              name={`${selectedStudent.firstName} ${selectedStudent.lastName}`}
              src={selectedStudent.avatarUrl}
              size="lg"
              ring={3}
              ringColor={accentColor}
              shadow="md"
            />

            <VStack gap={2} align="start" flex={1}>
              <Heading size="md" lineHeight="shorter">
                {`${selectedStudent?.firstName} ${selectedStudent?.middleName} ${selectedStudent?.lastName}`}
              </Heading>
              <Text
                color={colors.text.secondary}
                fontSize="xs"
                fontWeight="medium"
              >
                Student ID: {selectedStudent?.publicId}
              </Text>

              <HStack gap={3} wrap="wrap">
                {hasRoleOrHigher(Role.Editor) && (
                  <Button
                    colorPalette={colors.schoolAccent.primary}
                    variant="surface"
                    size="2xs"
                    fontWeight="medium"
                    onClick={() => handleEditStudent(selectedStudent)}
                  >
                    <Icon as={icons.utility.edit} size={"xs"} fontSize={"xs"} />
                    Edit Student
                  </Button>
                )}
                <Button
                  colorPalette={colors.schoolAccent.primary}
                  variant="surface"
                  size="2xs"
                  fontWeight="medium"
                  onClick={() => handleViewResults(selectedStudent)}
                >
                  <Icon as={icons.people.user} size={"xs"} />
                  View Results
                </Button>
              </HStack>
            </VStack>
          </HStack>
        </Cad>

        {/* Student Details Card */}

        <Cad>
          <SimpleGrid columns={2} gap={4}>
            <VStack gap={3} align="start">
              <HStack gap={3}>
                <Icon
                  as={icons.people.students}
                  color={`${accentColor}.400`}
                  boxSize={5}
                />
                <Box>
                  <Text
                    fontSize="xs"
                    color={colors.text.secondary}
                    fontWeight="medium"
                  >
                    CLASS
                  </Text>
                  <Text fontWeight="bold" fontSize="sm">
                    {classMap[selectedStudent?.currentClassId]}
                  </Text>
                </Box>
              </HStack>

              <HStack gap={3}>
                <Icon
                  as={icons.people.user}
                  color={`${accentColor}.400`}
                  boxSize={5}
                />
                <Box>
                  <Text
                    fontSize="xs"
                    color={colors.text.secondary}
                    fontWeight="medium"
                  >
                    GENDER
                  </Text>
                  <Text fontWeight="bold" fontSize="sm">
                    {genderMap[selectedStudent?.gender]}
                  </Text>
                </Box>
              </HStack>
            </VStack>

            <VStack gap={3} align="start">
              <HStack gap={3}>
                <Icon
                  as={icons.utility.footprints}
                  color={`${accentColor}.400`}
                  boxSize={5}
                />
                <Box>
                  <Text
                    fontSize="xs"
                    color={colors.text.secondary}
                    fontWeight="medium"
                  >
                    AGE
                  </Text>
                  <Text fontWeight="bold" fontSize="sm">
                    {calculateAge(selectedStudent?.dateOfBirth || "")} years
                  </Text>
                </Box>
              </HStack>

              <HStack gap={3}>
                <Icon
                  as={icons.utility.check}
                  color={`${accentColor}.400`}
                  boxSize={5}
                />
                <Box>
                  <Text
                    fontSize="xs"
                    color={colors.text.secondary}
                    fontWeight="medium"
                  >
                    STATUS
                  </Text>
                  <Badge
                    colorPalette={
                      studentStatusMap[selectedStudent.status].color
                    }
                    fontSize="xs"
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    {studentStatusMap[selectedStudent.status].name}
                  </Badge>
                </Box>
              </HStack>
            </VStack>
          </SimpleGrid>

          <Separator my={4} />

          {/* Classmates Section */}
          <VStack gap={3} align="start">
            <Text fontSize="sm" fontWeight="bold" color={colors.text.secondary}>
              CLASSMATES
            </Text>
            <Flex
              gap={3}
              w="100%"
              align="center"
              onClick={() => handleSameClass(selectedStudent.currentClassId)}
            >
              <AvatarGroup spaceX="-25px" size="md">
                {fourClassStudents.map((student) => (
                  <Avatar
                    key={student.id}
                    name={`${student.firstName} ${student.lastName}`}
                    src={student.avatarUrl}
                  />
                ))}
                {classCount(selectedStudent) - fourClassStudents.length - 1 >
                  0 && (
                  <Avatar
                    colorPalette={colors.schoolAccent.primary}
                    fallback={
                      "+" +
                      (classCount(selectedStudent) -
                        fourClassStudents.length -
                        1)
                    }
                    size="md"
                    ringColor={colors.background.primary}
                    shadow="md"
                  />
                )}
              </AvatarGroup>
              <Button
                variant="ghost"
                size="sm"
                colorPalette={colors.schoolAccent.primary}
                fontSize="xs"
                fontWeight="medium"
                _hover={{ bg: colors.background.secondary }}
                whiteSpace="normal"
                textAlign="left"
                minH="auto"
                h="auto"
                py={2}
                px={3}
                maxW={{ base: "100%", md: "150px" }}
                onClick={() => handleSameClass(selectedStudent.currentClassId)}
              >
                {getStudentClassNumberMessage(classCount(selectedStudent) - 1)}
              </Button>
            </Flex>
          </VStack>
        </Cad>

        {/* Guardians Section */}
        <Box w="100%">
          <HStack justify="space-between" mb={4}>
            <Heading size="md" color={colors.text.primary}>
              Guardians
            </Heading>
            {hasRoleOrHigher(1) && (
              <Tooltip
                content="Add Guardian"
                positioning={{ placement: "left" }}
                showArrow
              >
                <IconButton
                  variant="surface"
                  size="xs"
                  aria-label="add guardian"
                  shadow="md"
                  _hover={{
                    transform: "scale(1.1)",
                  }}
                  transition="all 0.2s"
                  onClick={handleAddOpenGuardian}
                >
                  <Icon as={icons.people.guardian} />
                </IconButton>
              </Tooltip>
            )}
          </HStack>

          {selectedStudent.guardians?.length === 0 ? (
            <Card.Root
              bg={colors.background.secondary}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={colors.border.secondary}
              minH="200px"
            >
              <Card.Body
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <VStack gap={4}>
                  <Icon
                    as={icons.people.guardian}
                    boxSize={12}
                    color={colors.text.secondary}
                  />
                  <Text
                    textAlign="center"
                    color={colors.text.secondary}
                    fontSize="sm"
                  >
                    No guardians added for this student
                  </Text>
                  {hasRoleOrHigher(Role.Editor) && (
                    <Button
                      variant="outline"
                      colorPalette={colors.schoolAccent.primary}
                      size="sm"
                      fontWeight="medium"
                      onClick={handleAddOpenGuardian}
                    >
                      <Icon as={icons.people.guardian} />
                      Add Guardian
                    </Button>
                  )}
                </VStack>
              </Card.Body>
            </Card.Root>
          ) : (
            <VStack gap={4} align="stretch">
              {selectedStudent?.guardians
                .filter((guardian) => guardian && guardian.guardianId)
                .map((guardian) => (
                  <GuardianCard
                    key={guardian.guardianId}
                    guardian={guardian}
                    user={user}
                    editorPolicy={editorPolicy}
                    accentColor={accentColor}
                    onEditGuardian={handleEditGuardian}
                  />
                ))}
            </VStack>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default StudentInfoPanel;
